import { capitalize } from 'lodash-es'
import React, { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { isDefined } from 'ts-extras'
import { useTranslations } from '~/components/Locale'
import type { ArrayElement, Product, Variant } from '~/types'
import { cn, getPrice, gidToId, toConvergeProduct } from '~/utils'
import { VariantOption } from './VariantOption'

export interface Props {
	product: Omit<Product, 'priceRange' | 'compareAtPriceRange'>
	onChange?: (v: Variant, optionIndex: [number, number]) => void
	stage?: number
}

export const VariantSelector: React.FC<Props> = ({ product, onChange, stage = 999 }) => {
	const variants = product.variants
	const t = useTranslations('react.actions')
	const [searchParams, setSearchParams] = useSearchParams()
	const preselected =
		product.variants.find(v => gidToId(v.id).toString() === searchParams.get('variant')) ??
		product.variants.find(v => v.id === product.defaultVariant?.value)
	// Use preselected, or find the first variant that is in stock
	const selected = preselected ?? product.variants.find(v => v.availableForSale) ?? product.variants[0]
	const options = getAvailableProductOptions(product)
	const [optionIndices, setIndices] = useState<[number, number]>([preselected ? options.length : stage, -1])

	useEffect(() => {
		if (onChange && preselected) onChange(preselected, optionIndices)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	return (
		<>
			{options.map((opt, optIdx) => {
				const prices = findVariantPrices(selected, variants, opt)
				const labels = findVariantLabels(variants[0], variants, opt)
				const disabled = stage < optIdx - 1
				return (
					<fieldset
						key={opt.name}
						className={cn('grid grid-cols-2 gap-4', { 'opacity-step pointer-events-none': disabled })}
					>
						<legend className="mb-1 whitespace-nowrap text-xl font-semibold">
							{capitalize(`${t?.choose ?? ''} ${opt.name.toLocaleLowerCase()}`.trim())}
						</legend>
						{opt.optionValues.map((value, valIdx) => {
							const variant = findVariant(selected, variants, opt.name, value.name)
							if (!variant) return null
							const isSelected = isOptionSelected(selected, opt.name, value.name) && stage >= optIdx
							return (
								<VariantOption
									key={`${opt.name}-${value.name}`}
									name={opt.name}
									checked={isSelected}
									disabled={disabled || !variant.availableForSale}
									variant={variant}
									option={value}
									prefer={product.handle === 'auk-mini' && optIdx === 0 ? 'color' : undefined}
									price={prices[valIdx]}
									badge={labels[valIdx]}
									onChange={() => {
										const varr = findVariant(selected, variants, opt.name, value.name)
										setIndices([optIdx, valIdx])
										if (varr) {
											setSearchParams({ variant: gidToId(varr.id).toString() })
											window.cvg({
												method: 'track',
												eventName: 'Customize Product',
												properties: toConvergeProduct(varr)
											})
											onChange?.(varr, [optIdx, valIdx])
										}
									}}
								>
									{value.name}
								</VariantOption>
							)
						})}
					</fieldset>
				)
			})}
		</>
	)
}

/**
 * Returns the product options for the given product that are available for sale.
 * @param product
 * @returns
 */
export function getAvailableProductOptions(product: Omit<Product, 'priceRange' | 'compareAtPriceRange'>) {
	const selected = product.variants.find(v => v.availableForSale) ?? product.variants[0]
	const options = product.options.map(opt => ({
		name: opt.name,
		optionValues: opt.optionValues.filter(val => findVariant(selected, product.variants, opt.name, val.name))
	}))
	// Only show options with more than one choice
	return options.filter(o => o.optionValues.length > 1)
}

function findVariant(selected: Variant | null, variants: Variant[], name: string, val: string) {
	return variants.find(v =>
		v.selectedOptions.every(o => (name == o.name ? val == o.value : isOptionSelected(selected, o.name, o.value)))
	)
}

function isOptionSelected(v: Variant | null, name: string, val: string) {
	return v?.selectedOptions.find(opt => opt.name == name && opt.value == val) !== undefined
}

function findVariantPrices(selected: Variant | null, variants: Variant[], option: ArrayElement<Product['options']>) {
	const options = option.optionValues
		.map(val => findVariant(selected, variants, option.name, val.name))
		.filter(isDefined)
		.map(v => ({
			name: v.selectedOptions.find(o => o.name === option.name)?.value,
			price: getPrice(v.price)
		}))

	const lowestPrice = Math.min(...options.map(o => o.price))
	return options.map(o => o.price - lowestPrice)
}

function findVariantLabels(selected: Variant | null, variants: Variant[], option: ArrayElement<Product['options']>) {
	return option.optionValues
		.map(val => findVariant(selected, variants, option.name, val.name))
		.map(v => v?.offer?.value)
}
